<template>
  <div v-if="show" class="contrat-wizard-overlay" @click.self="closeWizard">
    <div class="contrat-wizard-container">
      <!-- Header with step indicator -->
      <div class="wizard-header">
        <h2>{{ wizardTitle }}</h2>
        <button class="close-btn" @click="closeWizard">&times;</button>
      </div>

      <!-- Step indicator -->
      <div class="step-indicator">
        <div 
          v-for="(step, index) in steps" 
          :key="index"
          class="step-item"
          :class="{ 
            'active': currentStep === index + 1, 
            'completed': currentStep > index + 1 
          }"
        >
          <div class="step-circle">{{ index + 1 }}</div>
          <span class="step-label">{{ step.label }}</span>
        </div>
      </div>

      <!-- Step content -->
      <div class="wizard-content">
        <!-- Step 1: Client Selection/Creation -->
        <div v-if="currentStep === 1" class="step-content">
          <ClientStepWizard 
            v-model:selectedClient="selectedClient"
            @client-selected="handleClientSelected"
            @next="goToNextStep"
          />
        </div>

        <!-- Step 2: Property (Bien) Selection/Creation -->
        <div v-if="currentStep === 2" class="step-content">
          <BienStepWizard 
            v-model:selectedBien="selectedBien"
            @bien-selected="handleBienSelected"
            @next="goToNextStep"
            @previous="goToPreviousStep"
          />
        </div>

        <!-- Step 3: Contract Creation -->
        <div v-if="currentStep === 3" class="step-content">
          <ContratStepWizard 
            :client="selectedClient"
            :bien="selectedBien"
            @contract-created="handleContractCreated"
            @previous="goToPreviousStep"
          />
        </div>
      </div>

      <!-- Navigation buttons -->
      <div class="wizard-footer">
        <button 
          v-if="currentStep > 1" 
          @click="goToPreviousStep"
          class="btn-secondary"
        >
          Précédent
        </button>
        <div class="spacer"></div>
        <button 
          v-if="currentStep < 3" 
          @click="goToNextStep"
          class="btn-primary"
          :disabled="!canProceed"
        >
          Suivant
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ClientStepWizard from './wizard-steps/ClientStepWizard.vue'
import BienStepWizard from './wizard-steps/BienStepWizard.vue'
import ContratStepWizard from './wizard-steps/ContratStepWizard.vue'

export default {
  name: 'ContratWizard',
  components: {
    ClientStepWizard,
    BienStepWizard,
    ContratStepWizard
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'contract-created'],
  data() {
    return {
      currentStep: 1,
      selectedClient: null,
      selectedBien: null,
      steps: [
        { label: 'Client', key: 'client' },
        { label: 'Bien', key: 'bien' },
        { label: 'Contrat', key: 'contrat' }
      ]
    }
  },
  computed: {
    wizardTitle() {
      const titles = {
        1: 'Étape 1: Sélection du Client',
        2: 'Étape 2: Sélection du Bien',
        3: 'Étape 3: Création du Contrat'
      }
      return titles[this.currentStep] || 'Nouveau Contrat'
    },
    canProceed() {
      switch (this.currentStep) {
        case 1:
          return this.selectedClient !== null
        case 2:
          return this.selectedBien !== null
        case 3:
          return true
        default:
          return false
      }
    }
  },
  methods: {
    goToNextStep() {
      if (this.currentStep < 3 && this.canProceed) {
        this.currentStep++
      }
    },
    goToPreviousStep() {
      if (this.currentStep > 1) {
        this.currentStep--
      }
    },
    handleClientSelected(client) {
      this.selectedClient = client
    },
    handleBienSelected(bien) {
      this.selectedBien = bien
    },
    handleContractCreated(contract) {
      this.$emit('contract-created', contract)
      this.resetWizard()
    },
    resetWizard() {
      this.currentStep = 1
      this.selectedClient = null
      this.selectedBien = null
    },
    closeWizard() {
      this.resetWizard()
      this.$emit('close')
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.resetWizard()
      }
    }
  }
}
</script>

<style scoped>
.contrat-wizard-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(34, 48, 90, 0.8), rgba(203, 184, 124, 0.3));
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.contrat-wizard-container {
  background: white;
  border-radius: 20px;
  width: 92%;
  max-width: 1200px;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
  animation: slideUp 0.4s ease-out;
  overflow: hidden;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.wizard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 40px;
  border-bottom: none;
  background: linear-gradient(135deg, #22305a 0%, #2c3e50 50%, #34495e 100%);
  color: white;
  position: relative;
  overflow: hidden;
}

.wizard-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(203, 184, 124, 0.1) 0%, transparent 50%);
  pointer-events: none;
}

.wizard-header h2 {
  margin: 0;
  font-size: 1.8rem;
  font-weight: 700;
  background: linear-gradient(45deg, #ffffff, #cbb87c);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.close-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: #cbb87c;
  transform: rotate(90deg) scale(1.1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.step-indicator {
  display: flex;
  justify-content: center;
  padding: 40px 32px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-bottom: 1px solid rgba(203, 184, 124, 0.2);
  position: relative;
}

.step-indicator::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, #cbb87c, transparent);
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  max-width: 180px;
  transition: all 0.3s ease;
}

.step-item:hover {
  transform: translateY(-2px);
}

.step-item:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 20px;
  right: -50%;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #e2e8f0, #f1f5f9);
  z-index: 1;
  border-radius: 2px;
}

.step-item.completed:not(:last-child)::after {
  background: linear-gradient(90deg, #10b981, #059669);
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
}

.step-circle {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e2e8f0, #f1f5f9);
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  position: relative;
  z-index: 2;
  margin-bottom: 12px;
  transition: all 0.4s ease;
  border: 3px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.step-item.active .step-circle {
  background: linear-gradient(135deg, #22305a, #2c3e50);
  color: white;
  transform: scale(1.1);
  border-color: #cbb87c;
  box-shadow: 0 6px 20px rgba(34, 48, 90, 0.4), 0 0 0 4px rgba(203, 184, 124, 0.3);
}

.step-item.completed .step-circle {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border-color: #ecfdf5;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.step-item.completed .step-circle::after {
  content: '✓';
  position: absolute;
  font-size: 0.9rem;
  font-weight: bold;
}

.step-label {
  font-size: 0.95rem;
  color: #64748b;
  font-weight: 600;
  text-align: center;
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
}

.step-item.active .step-label {
  color: #22305a;
  font-weight: 700;
  transform: scale(1.05);
}

.step-item.completed .step-label {
  color: #10b981;
  font-weight: 700;
}

.wizard-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.step-content {
  min-height: 450px;
  padding: 40px;
  position: relative;
}

.wizard-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 40px;
  border-top: 1px solid rgba(203, 184, 124, 0.2);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  position: relative;
}

.wizard-footer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, #cbb87c, transparent);
}

.spacer {
  flex: 1;
}

/* Responsive Design */
@media (max-width: 768px) {
  .contrat-wizard-container {
    width: 95%;
    max-height: 98vh;
    border-radius: 15px;
  }
  
  .wizard-header {
    padding: 20px 24px;
  }
  
  .wizard-header h2 {
    font-size: 1.4rem;
  }
  
  .step-indicator {
    padding: 24px 16px;
  }
  
  .step-circle {
    width: 36px;
    height: 36px;
    font-size: 1rem;
  }
  
  .step-label {
    font-size: 0.8rem;
  }
  
  .step-content {
    padding: 24px 20px;
    min-height: 350px;
  }
  
  .wizard-footer {
    padding: 20px 24px;
  }
  
  .btn-primary, .btn-secondary {
    padding: 12px 24px;
    font-size: 0.9rem;
  }
}

.btn-primary, .btn-secondary {
  padding: 14px 32px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-primary {
  background: linear-gradient(135deg, #22305a, #2c3e50);
  color: white;
  box-shadow: 0 4px 15px rgba(34, 48, 90, 0.3);
}

.btn-primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(203, 184, 124, 0.3), transparent);
  transition: left 0.5s;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #1a2647, #243346);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(34, 48, 90, 0.4);
}

.btn-primary:hover:not(:disabled)::before {
  left: 100%;
}

.btn-primary:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-secondary {
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  color: #475569;
  border: 2px solid #d1d5db;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.btn-secondary:hover {
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  border-color: #22305a;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}
</style>